import { createTheme } from '@mui/material/styles';

// ============================================================
//  D&D Planner — premium dark theme
//  Crimson + gold on near-black. Cinzel (display) + Inter (UI).
// ============================================================

const CRIMSON = '#D8352A';
const CRIMSON_LIGHT = '#EA5A4F';
const CRIMSON_DARK = '#A31E15';
const GOLD = '#C9A45C';
const EMERALD = '#35B37E';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: CRIMSON, light: CRIMSON_LIGHT, dark: CRIMSON_DARK, contrastText: '#ffffff' },
    secondary: { main: GOLD, light: '#DCC085', dark: '#A07E3D', contrastText: '#1A160C' },
    success: { main: EMERALD, light: '#5AC79A', dark: '#248A5F', contrastText: '#04150E' },
    background: { default: '#0F1014', paper: '#17191F' },
    divider: 'rgba(255,255,255,0.09)',
    text: { primary: '#EAECEF', secondary: '#9AA1AC' },
  },

  shape: { borderRadius: 12 },

  typography: {
    fontFamily: '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    h1: { fontFamily: '"Cinzel", serif', fontWeight: 700, letterSpacing: '0.02em' },
    h2: { fontFamily: '"Cinzel", serif', fontWeight: 700, letterSpacing: '0.02em' },
    h3: { fontFamily: '"Cinzel", serif', fontWeight: 600, letterSpacing: '0.03em' },
    h4: { fontFamily: '"Cinzel", serif', fontWeight: 600, letterSpacing: '0.03em' },
    h5: { fontFamily: '"Cinzel", serif', fontWeight: 600, letterSpacing: '0.04em' },
    h6: { fontWeight: 600, letterSpacing: '0.01em' },
    button: { fontWeight: 600, textTransform: 'none', letterSpacing: '0.01em' },
    overline: { fontFamily: '"Cinzel", serif', letterSpacing: '0.28em', fontWeight: 600 },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          minHeight: '100%',
          backgroundColor: '#0F1014',
          backgroundImage:
            'radial-gradient(1200px 600px at 50% -10%, rgba(216,53,42,0.16), transparent 60%),' +
            'radial-gradient(900px 500px at 100% 0%, rgba(201,164,92,0.07), transparent 55%)',
          backgroundAttachment: 'fixed',
        },
        '*::-webkit-scrollbar': { width: 10, height: 10 },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(255,255,255,0.12)',
          borderRadius: 8,
          border: '2px solid #0F1014',
        },
        '*::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#17191F',
          border: '1px solid rgba(255,255,255,0.08)',
        },
        rounded: { borderRadius: 16 },
      },
    },

    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: 'rgba(15,16,20,0.72)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          color: '#EAECEF',
        },
      },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 10, paddingTop: 9, paddingBottom: 9 },
        containedPrimary: {
          background: `linear-gradient(180deg, ${CRIMSON_LIGHT}, ${CRIMSON})`,
          boxShadow: '0 6px 18px rgba(216,53,42,0.30)',
          '&:hover': {
            background: `linear-gradient(180deg, ${CRIMSON}, ${CRIMSON_DARK})`,
            boxShadow: '0 8px 24px rgba(216,53,42,0.42)',
          },
        },
        outlined: {
          borderColor: 'rgba(255,255,255,0.16)',
          color: '#EAECEF',
          '&:hover': { borderColor: 'rgba(255,255,255,0.32)', backgroundColor: 'rgba(255,255,255,0.04)' },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 8, fontWeight: 500 },
        filledSuccess: {
          backgroundColor: 'rgba(53,179,126,0.16)',
          color: '#7EDCB4',
          border: '1px solid rgba(53,179,126,0.34)',
        },
      },
    },

    MuiAvatar: {
      styleOverrides: {
        root: { border: '1px solid rgba(255,255,255,0.14)' },
      },
    },
  },
});

export default theme;
